import { DOCUMENT } from "@angular/common";
import { computed, DestroyRef, effect, inject, Service, signal } from "@angular/core";

import { applyChartTheme } from "../../core/utils/chart-theme";
import { Constants } from "../constants/constants";

export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

const PREFERENCES: ThemePreference[] = ["system", "light", "dark"];

@Service()
export class ThemeService {
  private _document = inject(DOCUMENT);
  private _window = this._document.defaultView;
  private _darkQuery = this._window?.matchMedia?.("(prefers-color-scheme: dark)");

  preference = signal<ThemePreference>(this._readPreference());
  systemTheme = signal<ResolvedTheme>(this._darkQuery?.matches ? "dark" : "light");

  resolved = computed<ResolvedTheme>(() => {
    const preference = this.preference();
    return preference === "system" ? this.systemTheme() : preference;
  });

  constructor() {
    effect(() => {
      this._apply(this.resolved());
      applyChartTheme(this.resolved());
    });

    const onSystemChange = (event: MediaQueryListEvent) => this.systemTheme.set(event.matches ? "dark" : "light");
    const onBeforePrint = () => this._apply("light");
    const onAfterPrint = () => this._apply(this.resolved());

    this._darkQuery?.addEventListener("change", onSystemChange);
    this._window?.addEventListener("beforeprint", onBeforePrint);
    this._window?.addEventListener("afterprint", onAfterPrint);

    inject(DestroyRef).onDestroy(() => {
      this._darkQuery?.removeEventListener("change", onSystemChange);
      this._window?.removeEventListener("beforeprint", onBeforePrint);
      this._window?.removeEventListener("afterprint", onAfterPrint);
    });
  }

  public setPreference(preference: ThemePreference): void {
    this.preference.set(preference);

    try {
      localStorage.setItem(Constants.localStorageKeys.theme, preference);
    } catch {
      // Storage can be blocked (private mode, disabled site data); the choice then lasts for this session only.
    }
  }

  public toggle(): void {
    this.setPreference(this.resolved() === "dark" ? "light" : "dark");
  }

  private _readPreference(): ThemePreference {
    try {
      const stored = localStorage.getItem(Constants.localStorageKeys.theme) as ThemePreference | null;
      return stored && PREFERENCES.includes(stored) ? stored : Constants.defaultThemePreference;
    } catch {
      return Constants.defaultThemePreference;
    }
  }

  private _apply(theme: ResolvedTheme): void {
    this._document.documentElement.dataset["bsTheme"] = theme;
  }
}
